// ============================================================================
// PositionsTab.jsx
// Current employees by cost center and position for one month, beside what was
// budgeted. Budget numbers do not exist in the portal yet, so that column is
// deliberately blank: a dash, never a zero, which would read as "budgeted for
// nobody".
// ============================================================================

import { c, t, card, tbl } from '../../theme';
import CostCenterTrend from './CostCenterTrend';

const n0 = (v) => Number(v ?? 0).toLocaleString();
const monthLabel = (ym) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const s = {
  filters: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  label:   { ...t.label, color: c.textMuted, display: 'flex', alignItems: 'center', gap: 6 },
  input:   { ...t.bodySm, padding: '6px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: 6,
             fontFamily: 'inherit', color: c.textStrong, background: c.surface },
};

export default function PositionsTab({ data, onMonthChange, costCenter, onCostCenterChange }) {
  const all = data?.positions || [];
  // The picked cost center stays an option even in a month it has no roster
  // for, so the table says it is empty rather than jumping to everyone.
  const costCenters = [...new Set(all.map((p) => p.raw_cost_center))];
  if (costCenter && !costCenters.includes(costCenter)) costCenters.push(costCenter);
  costCenters.sort();

  const rows = costCenter ? all.filter((p) => p.raw_cost_center === costCenter) : all;
  const total = rows.reduce((sum, p) => sum + p.heads, 0);

  return (
    <div>
      <div style={s.filters}>
        <label style={s.label}>
          Month
          <select style={s.input} value={data?.month || ''} onChange={(e) => onMonthChange(e.target.value)}>
            {(data?.months || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </label>
        <label style={s.label}>
          Cost Center
          <select id="positions-cost-center" style={s.input} value={costCenter} onChange={(e) => onCostCenterChange(e.target.value)}>
            <option value="">All cost centers</option>
            {costCenters.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
      </div>

      {/* Same position counts across a range of months. Follows the Cost
          Center picker above; the Month picker belongs to the table below. */}
      <CostCenterTrend kind="positions" costCenter={costCenter} />

      <div style={{ ...card.base, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: c.surfaceSunk }}>
              <th style={tbl.th}>Cost Center</th>
              <th style={tbl.th}>Position</th>
              <th style={tbl.thR}>Current</th>
              <th style={tbl.thR}>Budgeted</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={`${p.raw_cost_center}|${p.position}`} style={tbl.tr}>
                <td style={tbl.td}>{p.raw_cost_center}</td>
                <td style={tbl.td}>{p.position}</td>
                <td style={tbl.tdR}>{n0(p.heads)}</td>
                <td style={{ ...tbl.tdR, color: c.textFaint }}>—</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...tbl.td, color: c.textMuted }}>
                  No positions to show for this selection.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: c.surfaceSunk, fontWeight: 600 }}>
              <td style={tbl.td}>Total</td>
              <td style={tbl.td} />
              <td style={tbl.tdR}>{n0(total)}</td>
              <td style={{ ...tbl.tdR, color: c.textFaint }}>—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
